import { YStack, XStack, Text, styled, Anchor } from 'tamagui'
import { copy } from '../../content/copy'
import {
  LOGO_ALT,
  LOGO_SIZE_FOOTER,
  LOGO_WORDMARK_SRC,
} from '../../lib/logo'

const Section = styled(YStack, {
  name: 'FooterSection',
  paddingHorizontal: '$6',
  paddingVertical: '$10',
  backgroundColor: '$background',
  borderTopWidth: 1,
  borderTopColor: '$border',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$8',
  },
})

const Container = styled(YStack, {
  name: 'Container',
  maxWidth: 1200,
  marginHorizontal: 'auto',
  width: '100%',
  gap: '$8',
})

const TopRow = styled(XStack, {
  name: 'TopRow',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '$8',

  '$md': {
    flexDirection: 'column',
    gap: '$6',
  },
})

const LogoSection = styled(YStack, {
  name: 'LogoSection',
  gap: '$3',
})

const Logo = styled(Text, {
  name: 'Logo',
  fontFamily: '$heading',
  fontSize: '$7',
  fontWeight: '700',
  color: '$textPrimary',
})

const Tagline = styled(Text, {
  name: 'Tagline',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textSecondary',
  maxWidth: 300,
})

const NavSection = styled(XStack, {
  name: 'NavSection',
  gap: '$8',
  flexWrap: 'wrap',

  '$md': {
    gap: '$6',
  },
})

const NavColumn = styled(YStack, {
  name: 'NavColumn',
  gap: '$3',
})

const NavTitle = styled(Text, {
  name: 'NavTitle',
  fontFamily: '$body',
  fontSize: '$3',
  fontWeight: '600',
  color: '$textPrimary',
  marginBottom: '$1',
})

const NavLink = styled(Anchor, {
  name: 'NavLink',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textSecondary',
  textDecorationLine: 'none',
  cursor: 'pointer',

  hoverStyle: {
    color: '$orangePrimary',
  },
})

const Divider = styled(YStack, {
  name: 'Divider',
  height: 1,
  backgroundColor: '$border',
  width: '100%',
})

const BottomRow = styled(XStack, {
  name: 'BottomRow',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '$4',

  '$md': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
})

const Copyright = styled(Text, {
  name: 'Copyright',
  fontFamily: '$body',
  fontSize: '$2',
  color: '$textMuted',
})

const LegalLinks = styled(XStack, {
  name: 'LegalLinks',
  gap: '$4',
})

const LegalLink = styled(Anchor, {
  name: 'LegalLink',
  fontFamily: '$body',
  fontSize: '$2',
  color: '$textMuted',
  textDecorationLine: 'none',
  cursor: 'pointer',

  hoverStyle: {
    color: '$textSecondary',
  },
})

const SocialLinks = styled(XStack, {
  name: 'SocialLinks',
  gap: '$4',
})

const SocialLink = styled(Anchor, {
  name: 'SocialLink',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textSecondary',
  textDecorationLine: 'none',
  cursor: 'pointer',

  hoverStyle: {
    color: '$orangePrimary',
  },
})

export function FooterSection() {
  const { copyright, nav, legal, social } = copy.footer

  return (
    <Section>
      <Container>
        <TopRow>
          <LogoSection>
            <img
              src={LOGO_WORDMARK_SRC}
              alt={LOGO_ALT}
              height={LOGO_SIZE_FOOTER}
              style={{ height: LOGO_SIZE_FOOTER, width: 'auto' }}
            />
            <Tagline>AI-First Product Engineering</Tagline>
          </LogoSection>

          <NavSection>
            <NavColumn>
              <NavTitle>Services</NavTitle>
              {nav.slice(0, 2).map((item) => (
                <NavLink key={item.label} tag="a" href={item.href}>
                  {item.label}
                </NavLink>
              ))}
            </NavColumn>

            <NavColumn>
              <NavTitle>Company</NavTitle>
              {nav.slice(2).map((item) => (
                <NavLink key={item.label} tag="a" href={item.href}>
                  {item.label}
                </NavLink>
              ))}
              {legal.map((item) => (
                <NavLink key={item.label} tag="a" href={item.href}>
                  {item.label}
                </NavLink>
              ))}
            </NavColumn>

            <NavColumn>
              <NavTitle>Connect</NavTitle>
              {social.map((item) => (
                <SocialLink
                  key={item.label}
                  tag="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.label}
                </SocialLink>
              ))}
            </NavColumn>
          </NavSection>
        </TopRow>

        <Divider />

        <BottomRow>
          <Copyright>{copyright}</Copyright>

          <LegalLinks>
            {legal.map((item) => (
              <LegalLink key={item.label} tag="a" href={item.href}>
                {item.label}
              </LegalLink>
            ))}
          </LegalLinks>
        </BottomRow>
      </Container>
    </Section>
  )
}
